import { useRef, useState } from "react";

import styles from "@styles/forms-flow/creative.module.scss";

import {
  Button,
  Drawer,
  Label,
  Loading,
  Steps as StepsComponent,
} from "@components";

import { Environment } from "@core/Environment";

import { BenefitDetails } from "./components/BenefitDetails";
import { BenefitEnvironment } from "./components/BenefitEnvironment";
import { BenefitType } from "./components/BenefitType";
import { CreativeName } from "./components/CreativeName";
import { GainReminder } from "./components/GainReminder";
import { PlaceBenefitUse } from "./components/PlaceBenefitUse";
import { SpendReminder } from "./components/SpendReminder";
import { UploadEmailHtml } from "./components/UploadEmailHtml";
import { UrlType } from "./components/UrlType";

import BenefitVisibility from "@containers/beneficios/steps/BenefitVisibility";
import CreativeDetails from "@containers/flows/CreativeDetails";
import { OlgaEnvironmentsAPI } from "@providers/OlgaEnvironmentsAPI";
import { FlowAPI } from "@providers/FlowAPI";
import { FlowStorage } from "@storage/FlowStorage";
import mergeObjects from "lodash.merge";
import { useEffect } from "react";
import { ContemplationInterval } from "./components/ContemplationInterval";
import { If } from "shared/components/control-statements";

const Steps = {
  NAME: 1,
  URL: 2,
  CONTEMPLATION: 3,
  BENEFIT_TYPE: 4,
  BENEFIT_DETAILS: 5,
  BENEFIT_VISIBILITY: 6,
  PLACE_BENEFIT_USE: 7,
  PLACE_BENEFIT_BRAND: 8,
  MESSAGE_EMAIL: 9,
  FINISHED: 14,
};

const options = {
  REPORT: "REPORT",
  FORM: "FORM",
};

export const EmailCreativeActionFormOld = ({
  visible,
  onClose,
  flowType,
  node,
  restrictions,
  onChange,
  onSave,
  isCreativeAction,
}) => {
  const [steps, setSteps] = useState(Object.keys(Steps).length);
  const [currentStep, setCurrentStep] = useState(Steps.NAME);
  const [creative, setCreative] = useState({
    creative: node?.data?.creative || {},
    coupon: node?.data?.coupon || {},
  });
  const [isValid, setIsValid] = useState(false);
  const [brandsList, setBrands] = useState([]);
  const [showStartOptions, setShowStartOptions] = useState(false);
  const [step, setStep] = useState(null);
  const [report, setReport] = useState({});
  const [maxGainEnabled, setMaxGainEnabled] = useState(false);
  const [loading, setLoading] = useState(false);

  const getFreshNode = (data) => {
    return {
      ...node,
      data: {
        ...node.data,
        ...data,
      },
    };
  };
  const getBrands = async () => {
    try {
      const response = await OlgaEnvironmentsAPI.getBrands();
      const brands = response.getData([]);
      setBrands(brands);
    } catch (error) {
      setBrands([]);
    }
  };

  useEffect(() => {
    getBrands();
  }, []);

  useEffect(() => {
    if (restrictions?.lockCreativeBenefit) {
      setSteps(
        Object.values(Steps).filter((stepIndex) =>
          [Steps.NAME, Steps.URL, Steps.MESSAGE_EMAIL].includes(stepIndex)
        ).length
      );
    }
  }, [restrictions]);

  useEffect(() => {
    const c = {
      creative: node?.data?.creative || {},
      coupon: node?.data?.coupon || {},
    };
    setCreative(c);
    setIsValid(isValidStep(currentStep, c, flowType, maxGainEnabled));
  }, [node]);

  useEffect(() => {
    setIsValid(isValidStep(currentStep, creative, flowType, maxGainEnabled));
  }, [currentStep]);

  useEffect(() => {
    if (isCreativeAction) setShowStartOptions(true);
  }, [isCreativeAction]);

  const onChangeCreative = (branch, value) => {
    const newCreative = {
      ...creative,
      [branch]: {
        ...(creative[branch] || {}),
        ...value,
      },
    };

    setCreative(newCreative);
    setIsValid(isValidStep(currentStep, newCreative, flowType, maxGainEnabled));
    onChange && onChange(getFreshNode(newCreative));
    return getFreshNode(newCreative);
  };

  useEffect(() => {
    setIsValid(isValidStep(currentStep, creative, flowType, maxGainEnabled));
  }, []);

  const nextStep = (futureCreative) => {
    const nextStep = getNextStep(
      currentStep,
      futureCreative || creative,
      restrictions,
      brandsList || []
    );
    onSave && onSave(getFreshNode(futureCreative || creative), false);

    return nextStep;
  };

  const loadReport = async (flowId, stepId, creativeId) => {
    setLoading(true);
    const response = await FlowAPI.getFlowReports(flowId, stepId);
    const allCreatives = response.getData({ creatives: [] }).creatives;
    const selectedCreative = allCreatives.filter((c) => c.id === creativeId);

    setReport(selectedCreative[0]);
    setLoading(false);
  };

  useEffect(() => {
    if (visible) {
      const stepId = node?.data?.stepId;
      const flowId = node?.data?.flow_id;
      const creativeId = node?.data?.creative?.id;

      loadReport(flowId, stepId, creativeId);
    }
  }, [node?.data?.stepId]);

  const creativeName = useRef(creative?.creative?.name);
  const startedCreative = creativeName.current;

  return (
    <Drawer
      visible={visible}
      onClose={() => {
        onClose();
        setCurrentStep(Steps.NAME);

        if (showStartOptions) setStep(null);
      }}
      showBackButton={
        (currentStep > Steps.NAME && currentStep != Steps.FINISHED) ||
        (step && showStartOptions)
      }
      backButton={() =>
        step && currentStep === Steps.NAME
          ? setStep(null)
          : setCurrentStep(
              (prevState) => getPrevStep(prevState, creative, restrictions),
              getBrands
            )
      }
      title={"Adicionar um criativo - Email"}
    >
      {showStartOptions && !step && !!startedCreative && (
        <>
          <Label>O que deseja fazer?</Label>

          <div className={styles.options}>
            <div
              className={styles.option}
              onClick={() => setStep(options.REPORT)}
            >
              <div className="d-flex align-center">
                <div className={styles.icon}>
                  <i className="fal fa-list" />
                </div>
                <p>Ver relatório</p>
              </div>
              <div className={styles.chevron}>
                <i className="fas fa-chevron-right" />
              </div>
            </div>
            <div
              className={styles.option}
              onClick={() => setStep(options.FORM)}
            >
              <div className="d-flex align-center">
                <div className={styles.icon}>
                  <i className="fal fa-pen-to-square" />
                </div>
                <p>Editar disparo</p>
              </div>
              <div className={styles.chevron}>
                <i className="fas fa-chevron-right" />
              </div>
            </div>
          </div>
        </>
      )}

      {step === options.REPORT && (
        <>
          <If cond={loading}>
            <div className={styles.loadingReports}>
              <Loading />
            </div>
          </If>

          <If cond={!loading}>
            <CreativeDetails
              flowType={FlowStorage.getFlowType(flowType)}
              creative={creative}
              node={node}
              report={report}
            />
          </If>
        </>
      )}

      {(((step === options.FORM || !showStartOptions) && startedCreative) ||
        !startedCreative) && (
        <>
          <StepsComponent
            steps={steps}
            stepsIsArray
            currentStep={currentStep}
          />

          <div className={styles.scrollContainer}>
            {currentStep === Steps.NAME && (
              <CreativeName
                node={node}
                flowType={flowType}
                restrictions={restrictions}
                creative={creative?.creative}
                onChangeCreative={(data) => onChangeCreative("creative", data)}
              />
            )}

            {currentStep === Steps.URL && (
              <UrlType
                creative={creative?.creative}
                onChangeCreative={(data) => {
                  onChangeCreative("creative", data);
                }}
              />
            )}

            {currentStep === Steps.CONTEMPLATION && (
              <ContemplationInterval
                creative={creative?.creative}
                onChangeCreative={(data) => {
                  onChangeCreative("creative", data);
                }}
              />
            )}

            {currentStep === Steps.BENEFIT_TYPE && (
              <BenefitType
                selectedBenefit={creative?.coupon?.type}
                setSelectedBenefit={(type) => {
                  onChangeCreative("coupon", { type });
                }}
              />
            )}

            {currentStep === Steps.PLACE_BENEFIT_BRAND && (
              <BenefitEnvironment
                benefit={creative?.coupon}
                brands={brandsList}
                onChange={(data) => onChangeCreative("coupon", data)}
              />
            )}

            {currentStep === Steps.BENEFIT_DETAILS && (
              <BenefitDetails
                coupon={creative?.coupon}
                onChangeCreative={(data) => onChangeCreative("coupon", data)}
                onEnableMaxGain={(status) => setMaxGainEnabled(status)}
              />
            )}

            {currentStep === Steps.BENEFIT_VISIBILITY && (
              <BenefitVisibility
                benefit={creative?.coupon}
                onChange={(data) => onChangeCreative("coupon", data)}
              />
            )}

            {currentStep === Steps.PLACE_BENEFIT_USE && (
              <PlaceBenefitUse
                coupon={creative?.coupon}
                onChangeCreative={(data) => onChangeCreative("coupon", data)}
              />
            )}

            {currentStep === Steps.MESSAGE_EMAIL && (
              <UploadEmailHtml
                coupon={creative.coupon}
                url={creative?.creative?.url}
                message={creative.creative?.message}
                restrictions={restrictions}
                onChangeCreative={(data) =>
                  onChangeCreative("creative", { message: data })
                }
              />
            )}

            {currentStep === Steps.GAIN_REMINDER && (
              <GainReminder
                coupon={creative?.coupon}
                onChangeCreative={(data) => {
                  const tmp = creative?.coupon?.expiration || {};
                  onChangeCreative("coupon", {
                    expiration: mergeObjects(tmp, { gain: { remind: data } }),
                  });
                }}
              />
            )}
            {currentStep === Steps.GAIN_REMINDER_EMAIL && (
              <UploadEmailHtml
                coupon={creative?.coupon}
                url={creative?.creative?.url}
                message={creative.coupon?.expiration?.gain?.remind?.message}
                onChangeCreative={(data) => {
                  const tmp = creative?.coupon?.expiration || {};
                  onChangeCreative("coupon", {
                    expiration: mergeObjects(tmp, {
                      gain: { remind: { message: data } },
                    }),
                  });
                }}
              />
            )}

            {currentStep === Steps.SPEND_REMINDER && (
              <SpendReminder
                coupon={creative?.coupon}
                onChangeCreative={(data) => {
                  const tmp = creative?.coupon?.expiration || {};
                  onChangeCreative("coupon", {
                    expiration: mergeObjects(tmp, { spend: { remind: data } }),
                  });
                }}
              />
            )}
            {currentStep === Steps.SPEND_REMINDER_EMAIL && (
              <UploadEmailHtml
                coupon={creative?.coupon}
                url={creative?.creative?.url}
                message={creative?.coupon?.expiration?.spend?.remind?.message}
                onChangeCreative={(data) => {
                  const tmp = creative?.coupon?.expiration || {};
                  onChangeCreative("coupon", {
                    expiration: mergeObjects(tmp, {
                      spend: { remind: { message: data } },
                    }),
                  });
                }}
              />
            )}

            {currentStep === Steps.FINISHED && (
              <div className={styles.finished}>
                <i className="far fa-check" />
                <p>Seu disparo foi configurado com sucesso!</p>
              </div>
            )}
          </div>

          <div className={styles.footer}>
            <div className="d-flex flex-column w-100">
              {currentStep !== Steps.FINISHED && (
                <>
                  {currentStep === Steps.BENEFIT_TYPE && (
                    <Button
                      className="w-100 mb-15"
                      design="default-2"
                      onClick={() => {
                        const futureCreative = onChangeCreative("coupon", {
                          type: null,
                        });
                        setCurrentStep(nextStep(futureCreative));
                      }}
                    >
                      Não conceder benefícios
                    </Button>
                  )}
                  {currentStep === Steps.URL && (
                    <Button
                      className="w-100 mb-15"
                      design="default-2"
                      onClick={() => {
                        const futureCreative = onChangeCreative("creative", {
                          url: {
                            type: null,
                            url: null,
                          },
                        });
                        setCurrentStep(nextStep(futureCreative));
                      }}
                    >
                      Não inserir CTA
                    </Button>
                  )}

                  <Button
                    className="w-100"
                    disabled={!isValid}
                    onClick={() => setCurrentStep(nextStep())}
                    design="primary"
                  >
                    Próximo
                  </Button>
                </>
              )}

              {currentStep === Steps.FINISHED && (
                <Button
                  className="w-100"
                  onClick={() => {
                    setCurrentStep(Steps.NAME);
                    setCreative({});
                    onClose();
                  }}
                >
                  Voltar para fluxos
                </Button>
              )}
            </div>
          </div>
        </>
      )}
    </Drawer>
  );
};

const getNextStep = (
  currentStep,
  { creative, coupon },
  restrictions,
  brandsList = []
) => {
  if (restrictions?.lockCreativeBenefit == true) {
    switch (currentStep) {
      case Steps.NAME:
        return Steps.URL;
      case Steps.URL:
        return Steps.MESSAGE_EMAIL;
      case Steps.MESSAGE_EMAIL:
        return Steps.FINISHED;
      default:
        return currentStep;
    }
  }

  switch (currentStep) {
    case Steps.NAME:
      return Steps.URL;
    case Steps.URL:
      return Steps.CONTEMPLATION;
    case Steps.CONTEMPLATION:
      return Steps.BENEFIT_TYPE;
    case Steps.BENEFIT_TYPE:
      return coupon?.type ? Steps.BENEFIT_DETAILS : Steps.MESSAGE_EMAIL;
    case Steps.BENEFIT_DETAILS:
      return Steps.BENEFIT_VISIBILITY;
    case Steps.BENEFIT_VISIBILITY:
      return Steps.PLACE_BENEFIT_USE;
    case Steps.PLACE_BENEFIT_USE:
      if (coupon?.type && (brandsList || []).length > 1) {
        return Steps.PLACE_BENEFIT_BRAND;
      }
      return Steps.MESSAGE_EMAIL;

    case Steps.PLACE_BENEFIT_BRAND:
      return Steps.MESSAGE_EMAIL;

    case Steps.MESSAGE_EMAIL:
      // if (coupon?.type) {
      //   return Steps.GAIN_REMINDER;
      // }
      return Steps.FINISHED;
    case Steps.GAIN_REMINDER:
      if (coupon.expiration?.gain?.remind?.active) {
        return Steps.GAIN_REMINDER_EMAIL;
      }
      // else if (coupon?.type == 'CASHBACK') {
      // 	return Steps.SPEND_REMINDER;
      // }
      return Steps.FINISHED;
    case Steps.GAIN_REMINDER_EMAIL:
      // if (coupon?.type == 'CASHBACK') {
      // 	return Steps.SPEND_REMINDER;
      // }
      return Steps.FINISHED;
    // case Steps.SPEND_REMINDER:
    // 	if (coupon.expiration?.spend?.remind?.active) {
    // 		return Steps.SPEND_REMINDER_EMAIL;
    // 	}
    // 	return Steps.FINISHED;
    // case Steps.SPEND_REMINDER_EMAIL:
    // 	return Steps.FINISHED;
    default:
      return currentStep;
  }
};

const getPrevStep = (
  currentStep,
  { creative, coupon },
  restrictions,
  brandsList = []
) => {
  if (restrictions?.lockCreativeBenefit == true) {
    switch (currentStep) {
      case Steps.NAME:
        return null;
      case Steps.URL:
        return Steps.NAME;
      case Steps.MESSAGE_EMAIL:
        return Steps.URL;
      default:
        return currentStep;
    }
  }

  switch (currentStep) {
    case Steps.NAME:
      return null;
    case Steps.URL:
      return Steps.NAME;
    case Steps.CONTEMPLATION:
      return Steps.URL;
    case Steps.BENEFIT_TYPE:
      return Steps.CONTEMPLATION;
    case Steps.BENEFIT_DETAILS:
      return Steps.BENEFIT_TYPE;
    case Steps.BENEFIT_VISIBILITY:
      return Steps.BENEFIT_DETAILS;
    case Steps.PLACE_BENEFIT_USE:
      return Steps.BENEFIT_VISIBILITY;
    case Steps.PLACE_BENEFIT_BRAND:
      return Steps.PLACE_BENEFIT_USE;
    case Steps.MESSAGE_EMAIL:
      if (coupon?.type && brandsList && (brandsList || []).length > 1) {
        return Steps.PLACE_BENEFIT_BRAND;
      } else if (coupon?.type) {
        return Steps.PLACE_BENEFIT_USE;
      }
      return Steps.BENEFIT_TYPE;
    case Steps.GAIN_REMINDER:
      return Steps.MESSAGE_EMAIL;
    case Steps.GAIN_REMINDER_EMAIL:
      return Steps.GAIN_REMINDER;
    // case Steps.SPEND_REMINDER:
    // 	if (coupon.expiration?.gain?.remind?.active) {
    // 		return Steps.GAIN_REMINDER_EMAIL;
    // 	}
    // 	return Steps.GAIN_REMINDER;
    // case Steps.SPEND_REMINDER_EMAIL:
    // 	return Steps.SPEND_REMINDER;
    default:
      return currentStep;
  }
};

const checkTimeErrors = (visibility) => {
  const conflicts = [];

  visibility?.forEach((day) => {
    day.times.forEach((time) => {
      if (time.start === time.end || time.start >= time.end) {
        conflicts.push({
          weekDay: day.weekDay,
          conflictingTime: time,
        });
      }
    });
  });

  return conflicts.length > 0;
};

const isValidStep = (currentStep, data, flowType, maxGainEnabled) => {
  const { creative, coupon } = data;

  switch (currentStep) {
    case Steps.NAME:
      return !!creative?.name && !!(flowType == "EVENT" || creative?.sendTime);
    case Steps.URL:
      return (
        creative?.url?.type == "STANDARD" ||
        (creative?.url?.type == "CUSTOM" && creative?.url?.url)
      );
    case Steps.CONTEMPLATION:
      return !!creative?.contemplation && !!creative?.contemplation?.type;
    case Steps.BENEFIT_TYPE:
      return !!coupon.type;
    case Steps.PLACE_BENEFIT_BRAND:
      return !!(coupon?.brands_valid?.length > 0);
    case Steps.BENEFIT_DETAILS: {
      const isBaseValid =
        !!coupon.code &&
        !!coupon.min_order &&
        (Environment.id != 34 || (coupon.group || "").length > 0) &&
        (coupon?.type == "FREE_DELIVERY" || !!coupon?.value) &&
        coupon?.expiration?.gain?.days > 0;

      const hasSkus = coupon.skus?.length > 0;
      const hasProductName = (coupon.description || "").length > 0;

      const isCouponProductOk =
        ["PRODUCT_DISCOUNT", "PRODUCT_CASH_DISCOUNT"].includes(coupon.type) &&
        hasSkus &&
        hasProductName &&
        isBaseValid;

      if (isCouponProductOk) return true;

      if (
        !["CASHBACK", "PRODUCT_DISCOUNT", "PRODUCT_CASH_DISCOUNT"].includes(
          coupon.type
        )
      ) {
        return isBaseValid;
      }

      return (
        isBaseValid &&
        !!coupon?.expiration?.gain?.days > 0 &&
        (maxGainEnabled ? coupon.max_gain > 0 : true)
      );
      // && !!coupon?.expiration?.spend?.days;
    }
    case Steps.BENEFIT_VISIBILITY: {
      if (coupon.visibility?.length === 0) return false;

      const isVisibilityOk = !checkTimeErrors(coupon.visibility);

      return isVisibilityOk;
    }
    case Steps.PLACE_BENEFIT_USE:
      return !!coupon.type_use;
    case Steps.MESSAGE_EMAIL:
      return !!creative?.message?.subject && !!creative?.message?.filename;
    case Steps.GAIN_REMINDER: {
      const { active, days, time } = coupon?.expiration?.gain?.remind || {};
      return active === false || (!!days && !!time);
    }
    case Steps.GAIN_REMINDER_EMAIL: {
      const message = coupon?.expiration?.gain?.remind?.message;
      return !!message?.subject && !!message?.filename;
    }
    // case Steps.SPEND_REMINDER: {
    // 	const { active, days, time } = coupon?.expiration?.spend?.remind || {};
    // 	return active === false || (!!days && !!time);
    // }
    // case Steps.SPEND_REMINDER_EMAIL: {
    // 	const message = coupon?.expiration?.spend?.remind?.message;
    // 	return !!message?.subject && !!message?.filename;
    // }
  }

  return true;
};
